import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchCompanies, createCompany, updateCompany, deleteCompany, fetchCompanyStatement, getCurrency,
  recordCompanyAdvance, recordCompanyRefund,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Front Desk — Phase A4: company accounts (city ledger). Manage the companies
// the hotel bills on account and view each one's open statement.
const S = {
  en: {
    title: "Company accounts", subtitle: "City ledger — companies billed on account",
    back: "Back to rooms", name: "Company name", tin: "TIN", contact: "Contact",
    phone: "Phone", limit: "Credit limit", add: "Add company", save: "Save", cancel: "Cancel",
    edit: "Edit", del: "Delete", del_q: "Remove this company account?",
    none: "No company accounts yet. Add one to bill stays on account.",
    name_req: "Company name is required.", could_not_save: "Could not save.",
    statement: "Statement", charged: "Charged", paid: "Paid", balance: "Outstanding",
    no_items: "No stays billed to this company yet.", guest: "Guest", dates: "Dates", due: "Balance",
    lic: "Business licence no.", guarantee: "Guarantee / letter ref", address: "Address",
    deposits: "Deposits on hand", advances: "Advances", refunds: "Refunds", available: "Available credit",
    rec_adv: "Record advance", refund: "Refund", amount: "Amount", note: "Note (optional)",
    add_adv: "Add advance", do_refund: "Refund", refund_over: "Refund exceeds deposits on hand.",
    shortfall: "Shortfall (company owes)",
    over_warn: "⚠ Usage exceeds this company's deposits — bill the excess to the guest.",
  },
  am: {
    title: "የድርጅት ሂሳቦች", subtitle: "የከተማ ሌጀር — በሂሳብ የሚከፍሉ ድርጅቶች",
    back: "ወደ ክፍሎች ተመለስ", name: "የድርጅት ስም", tin: "ቲን", contact: "አድራሻ",
    phone: "ስልክ", limit: "የብድር ገደብ", add: "ድርጅት ጨምር", save: "አስቀምጥ", cancel: "ሰርዝ",
    edit: "አርትዕ", del: "አጥፋ", del_q: "ይህን የድርጅት ሂሳብ ማስወገድ?",
    none: "እስካሁን የድርጅት ሂሳቦች የሉም። ቆይታዎችን በሂሳብ ለመክፈል አንድ ይጨምሩ።",
    name_req: "የድርጅት ስም ያስፈልጋል።", could_not_save: "ማስቀመጥ አልተቻለም።",
    statement: "የሂሳብ መግለጫ", charged: "የተከፈለ ወጪ", paid: "የተከፈለ", balance: "ቀሪ ዕዳ",
    no_items: "እስካሁን ለዚህ ድርጅት የተመዘገበ ቆይታ የለም።", guest: "እንግዳ", dates: "ቀናት", due: "ቀሪ",
    lic: "የንግድ ፈቃድ ቁ.", guarantee: "የዋስትና / ደብዳቤ ማጣቀሻ", address: "አድራሻ",
    deposits: "ተይዞ ያለ ገንዘብ", advances: "ቅድሚያ ክፍያዎች", refunds: "ተመላሽ", available: "የሚገኝ ክሬዲት",
    rec_adv: "ቅድሚያ ክፍያ መዝግብ", refund: "ተመላሽ", amount: "መጠን", note: "ማስታወሻ (አማራጭ)",
    add_adv: "ቅድሚያ ጨምር", do_refund: "መልስ", refund_over: "ተመላሹ ከተያዘው ገንዘብ ይበልጣል።",
    shortfall: "ጉድለት (ድርጅቱ ይከፍላል)",
    over_warn: "⚠ አጠቃቀሙ ከድርጅቱ ተቀማጭ በልጧል — ትርፉን ለእንግዳው ያስከፍሉ።",
  },
  om: {
    title: "Herrega dhaabbataa", subtitle: "Leejarii magaalaa — dhaabbilee herregaan kaffalan",
    back: "Gara kutaalee deebi'i", name: "Maqaa dhaabbataa", tin: "TIN", contact: "Qunnamtii",
    phone: "Bilbila", limit: "Daangaa liqii", add: "Dhaabbata dabali", save: "Kaa'i", cancel: "Haqi",
    edit: "Gulaali", del: "Haqi", del_q: "Herrega dhaabbataa kana haquu?",
    none: "Ammatti herregni dhaabbataa hin jiru. Turtii herregaan kaffaluuf tokko dabali.",
    name_req: "Maqaan dhaabbataa barbaachisaa dha.", could_not_save: "Kaa'uu hin dandeenye.",
    statement: "Ibsa herregaa", charged: "Kaffalame", paid: "Kaffalame", balance: "Hafe",
    no_items: "Ammatti turtiin dhaabbata kanaaf hin galmoofne.", guest: "Keessummaa", dates: "Guyyoota", due: "Hafe",
    lic: "Lakk. hayyama daldalaa", guarantee: "Wabii / wabii xalayaa", address: "Teessoo",
    deposits: "Maallaqa qabame", advances: "Kaffaltii duraa", refunds: "Deebi'e", available: "Liqii jiru",
    rec_adv: "Kaffaltii duraa galmeessi", refund: "Deebisi", amount: "Hanga", note: "Yaadannoo (filannoo)",
    add_adv: "Kaffaltii duraa dabali", do_refund: "Deebisi", refund_over: "Deebiin maallaqa qabame caala.",
    shortfall: "Hir'ina (dhaabbatichi kaffala)",
    over_warn: "⚠ Itti fayyadamni qabsiisa dhaabbatichaa caaleera — hafe keessummaarraa kaffalchiisi.",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const empty = { name: "", tin: "", contact_name: "", phone: "", credit_limit: "",
                business_license_no: "", guarantee_ref: "", address: "" };

export default function CompaniesPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";

  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null);
  const [stmt, setStmt] = useState(null);
  const [advAmt, setAdvAmt] = useState("");
  const [advNote, setAdvNote] = useState("");
  const [refAmt, setRefAmt] = useState("");
  const [moneyErr, setMoneyErr] = useState("");
  const [moneyBusy, setMoneyBusy] = useState(false);

  const load = () => fetchCompanies().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) { setErr(t("name_req")); return; }
    const body = {
      name: form.name.trim(), tin: form.tin.trim() || null,
      contact_name: form.contact_name.trim() || null, phone: form.phone.trim() || null,
      credit_limit: form.credit_limit === "" ? null : Number(form.credit_limit),
      business_license_no: form.business_license_no.trim() || null,
      guarantee_ref: form.guarantee_ref.trim() || null,
      address: form.address.trim() || null,
    };
    try {
      if (editId) await updateCompany(editId, body); else await createCompany(body);
      setForm(empty); setEditId(null); load();
    } catch { setErr(t("could_not_save")); }
  };

  const startEdit = (c) => {
    setEditId(c.id);
    setForm({ name: c.name || "", tin: c.tin || "", contact_name: c.contact_name || "",
              phone: c.phone || "", credit_limit: c.credit_limit ?? "",
              business_license_no: c.business_license_no || "", guarantee_ref: c.guarantee_ref || "",
              address: c.address || "" });
  };
  const remove = async (c) => {
    if (!window.confirm(t("del_q"))) return;
    setRows((rs) => rs.filter((x) => x.id !== c.id));
    try { await deleteCompany(c.id); } catch { load(); }
  };
  const toggleStmt = async (c) => {
    if (openId === c.id) { setOpenId(null); setStmt(null); return; }
    setOpenId(c.id); setStmt(null); setAdvAmt(""); setAdvNote(""); setRefAmt(""); setMoneyErr("");
    try { setStmt(await fetchCompanyStatement(c.id)); } catch { setStmt({ items: [], charged_total: 0, paid_total: 0, balance: 0 }); }
  };

  const addAdvance = async (cid) => {
    setMoneyErr("");
    const amt = Number(advAmt);
    if (!(amt > 0)) { setMoneyErr(t("could_not_save")); return; }
    setMoneyBusy(true);
    try {
      await recordCompanyAdvance(cid, { amount: amt, ...(advNote.trim() ? { note: advNote.trim() } : {}) });
      setAdvAmt(""); setAdvNote("");
      setStmt(await fetchCompanyStatement(cid));
    } catch { setMoneyErr(t("could_not_save")); }
    finally { setMoneyBusy(false); }
  };

  const doRefund = async (cid) => {
    setMoneyErr("");
    const amt = Number(refAmt);
    if (!(amt > 0)) { setMoneyErr(t("could_not_save")); return; }
    setMoneyBusy(true);
    try {
      await recordCompanyRefund(cid, { amount: amt });
      setRefAmt("");
      setStmt(await fetchCompanyStatement(cid));
    } catch (e) { setMoneyErr(e?.response?.status === 400 ? t("refund_over") : t("could_not_save")); }
    finally { setMoneyBusy(false); }
  };

  const inp = { padding: 8, borderRadius: 8, border: "1px solid #d8dee2" };

  return (
    <div style={{ maxWidth: 860, margin: "0 auto" }}>
      <Link className="muted" to="/front-desk/rooms" style={{ fontSize: 13, textDecoration: "none" }}>← {t("back")}</Link>
      <h1 style={{ margin: "6px 0 2px" }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      <form onSubmit={submit} style={{ ...CARD, padding: 14, marginTop: 14, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("name")} style={{ ...inp, width: 170 }} />
        <input value={form.tin} onChange={(e) => setForm({ ...form, tin: e.target.value })} placeholder={t("tin")} style={{ ...inp, width: 120 }} />
        <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder={t("contact")} style={{ ...inp, width: 140 }} />
        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("phone")} style={{ ...inp, width: 120 }} />
        <input value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} placeholder={t("limit")} type="number" min="0" style={{ ...inp, width: 120 }} />
        <input value={form.business_license_no} onChange={(e) => setForm({ ...form, business_license_no: e.target.value })} placeholder={t("lic")} style={{ ...inp, width: 150 }} />
        <input value={form.guarantee_ref} onChange={(e) => setForm({ ...form, guarantee_ref: e.target.value })} placeholder={t("guarantee")} style={{ ...inp, width: 170 }} />
        <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder={t("address")} style={{ ...inp, width: 200 }} />
        <button className="btn primary small" type="submit">{editId ? t("save") : t("add")}</button>
        {editId && <button className="btn secondary small" type="button" onClick={() => { setEditId(null); setForm(empty); }}>{t("cancel")}</button>}
        {err && <span style={{ color: "#A32D2D", fontSize: 13, width: "100%" }}>{err}</span>}
      </form>

      {rows == null ? (
        <p className="muted" style={{ marginTop: 14 }}>…</p>
      ) : rows.length === 0 ? (
        <div style={{ ...CARD, padding: 20, marginTop: 14, textAlign: "center" }} className="muted">{t("none")}</div>
      ) : (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map((c) => (
            <div key={c.id} style={{ ...CARD, padding: "12px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  <div className="muted" style={{ fontSize: 13 }}>
                    {c.tin ? `${t("tin")} ${c.tin}` : ""}{c.contact_name ? ` · ${c.contact_name}` : ""}{c.phone ? ` · ${c.phone}` : ""}
                    {c.credit_limit != null ? ` · ${t("limit")} ${formatPrice(c.credit_limit, cur)}` : ""}
                  </div>
                </div>
                <button className="btn secondary small" onClick={() => toggleStmt(c)}>{t("statement")}</button>
                <button className="btn secondary small" onClick={() => startEdit(c)}>{t("edit")}</button>
                <button onClick={() => remove(c)} style={{ fontSize: 13, background: "none", border: "none", color: "#A32D2D", cursor: "pointer" }}>{t("del")}</button>
              </div>
              {openId === c.id && (
                <div style={{ marginTop: 10, borderTop: "1px solid #eef2f2", paddingTop: 10 }}>
                  {stmt == null ? <div className="muted" style={{ fontSize: 13 }}>…</div> : (
                    <>
                      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 8 }}>
                        <span className="muted" style={{ fontSize: 13 }}>{t("charged")}: <b>{formatPrice(stmt.charged_total || 0, cur)}</b></span>
                        <span className="muted" style={{ fontSize: 13 }}>{t("advances")}: <b>{formatPrice(stmt.advances_total || 0, cur)}</b></span>
                        <span className="muted" style={{ fontSize: 13 }}>{t("refunds")}: <b>{formatPrice(stmt.refunds_total || 0, cur)}</b></span>
                        <span className="muted" style={{ fontSize: 13 }}>{t("deposits")}: <b>{formatPrice(stmt.deposits_on_hand || 0, cur)}</b></span>
                        <span style={{ fontSize: 13, color: (stmt.available_credit || 0) < 0 ? "#8A5A0B" : "#1D7A44" }}>
                          {(stmt.available_credit || 0) < 0 ? t("shortfall") : t("available")}: <b>{formatPrice(Math.abs(stmt.available_credit || 0), cur)}</b>
                        </span>
                      </div>
                      {(stmt.available_credit || 0) < 0 && (stmt.advances_total || 0) > 0 && (
                        <div style={{ background: "#FBF3DD", border: "1px solid #F0E4BC", color: "#5b4a12", borderRadius: 8, padding: "7px 10px", fontSize: 13, marginBottom: 8 }}>
                          {t("over_warn")}
                        </div>
                      )}

                      {/* Advance / refund controls */}
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
                        <input value={advAmt} onChange={(e) => setAdvAmt(e.target.value)} placeholder={t("amount")} type="number" min="0" style={{ ...inp, width: 110 }} />
                        <input value={advNote} onChange={(e) => setAdvNote(e.target.value)} placeholder={t("note")} style={{ ...inp, width: 180 }} />
                        <button className="btn primary small" disabled={moneyBusy} onClick={() => addAdvance(c.id)}>{t("add_adv")}</button>
                        <span className="muted" style={{ fontSize: 12 }}>·</span>
                        <input value={refAmt} onChange={(e) => setRefAmt(e.target.value)} placeholder={t("refund")} type="number" min="0" style={{ ...inp, width: 110 }} />
                        <button className="btn secondary small" disabled={moneyBusy} onClick={() => doRefund(c.id)}>{t("do_refund")}</button>
                        {moneyErr && <span style={{ color: "#A32D2D", fontSize: 13 }}>{moneyErr}</span>}
                      </div>
                      {(stmt.items || []).length === 0 ? (
                        <div className="muted" style={{ fontSize: 13 }}>{t("no_items")}</div>
                      ) : (
                        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                          <thead><tr style={{ textAlign: "left", color: "#5b6472" }}>
                            <th style={{ padding: "2px 0" }}>{t("guest")}</th><th>{t("dates")}</th>
                            <th style={{ textAlign: "right" }}>{t("charged")}</th><th style={{ textAlign: "right" }}>{t("paid")}</th><th style={{ textAlign: "right" }}>{t("due")}</th>
                          </tr></thead>
                          <tbody>
                            {stmt.items.map((it) => (
                              <tr key={it.id}>
                                <td style={{ padding: "2px 0" }}>{it.guest_name}{it.invoice_no ? ` · ${it.invoice_no}` : ""}</td>
                                <td>{it.check_in_date} → {it.check_out_date}</td>
                                <td style={{ textAlign: "right" }}>{formatPrice(it.total, cur)}</td>
                                <td style={{ textAlign: "right" }}>{formatPrice(it.paid, cur)}</td>
                                <td style={{ textAlign: "right", color: it.balance_due > 0 ? "#8A5A0B" : "#1D7A44" }}>{formatPrice(it.balance_due, cur)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
